import { useEffect } from "react";

// Third party
import { useQuery } from "@tanstack/react-query";

// MUI
import { Divider, Grid } from "@mui/material";
// Subscriptions queries

import { LOCATION_SUBSCRIPTION_QUERY } from "@/Settings/api/AdministrationTools/subscriptions";

// Redux
import { RootState } from "@/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { actions as actionLocation } from "@/redux/reducers/Location";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useLocation } from "@Common/hooks/Location/useLocation";

// API
import { getOemMetaData } from "@/Settings/api/AdministrationTools/queries";

// Components
import AdministrationToolsLoader from "./AdministrationToolsLoader";
import CollapseArea from "@/Layouts/components/Basics/CollapseArea";
import LocationDetails from "./LocationDetails/index";
import StorePicture from "./StorePicture";
import LocationDealerCodes from "./LocationDealerCodes";

// Helpers
import { storageSetItem } from "@/Common/helpers/Storage/index";

// Types
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";
import { ILocation } from "@Common/interfaces/location";

// Constants
import actions from "@/Constants/actionsList";

type AdministrationToolsDealerInfoProps = {
	itemSelected: { elementId: string; yCoordinate: number };
	queryIsFinish: boolean;
};

export default function AdministrationToolsDealerInfo(props: Readonly<AdministrationToolsDealerInfoProps>) {
	const dispatch = useDispatch();
	const { useAuth } = useAuthorization();
	const { addSubscription } = useSubscriptions();

	const location = useLocation();
	const locations = useSelector((state: RootState) => state.location.locations);
	const { locationId = "", geoDetails = { countryCode: "" } } = location ?? {};

	const StorePictureAction = useAuth(actions.EDIT_STORE_PICTURES);

	/********************************************* QUERIES ************************************************/
	const { data: oemMetaData, isLoading: isLoadingOemMetaData } = useQuery({
		queryKey: ["oemMetaData", locationId],
		queryFn: async () => getOemMetaData({ locationId, countryCode: geoDetails.countryCode }),
		enabled: !!locationId && !!geoDetails?.countryCode,
	});

	// Subscriptions
	const { getSubscription } = useSubscriptions();
	const administrationToolsSubscriptions = getSubscription(["locationsSubscriptions", locationId]);
	administrationToolsSubscriptions?.subscriptions?.onUpdateLocation?.setCallback(
		(response: GraphqlSubscriptionResponse<"onUpdateLocation", ILocation>) => {
			const resultMutation = response?.data?.onUpdateLocation ?? {};

			const fieldsToUpdate = Object.fromEntries(
				Object.entries(resultMutation).filter(([key, value]) => key !== "_id" && key !== "locationId" && value !== null)
			);
			// Filter and merge properties in one step
			const updatedLocation: ILocation = {
				...location,
				...fieldsToUpdate,
			};

			dispatch(actionLocation.SET_LOCATION({ location: updatedLocation }));
			storageSetItem("location", updatedLocation);

			// Update locations list if it exists
			const updatedLocations = locations?.map((loc: ILocation) =>
				loc.locationId === updatedLocation.locationId
					? { ...loc, name: updatedLocation.name, timezone: updatedLocation.timezone }
					: loc
			);
			if (updatedLocations) {
				dispatch(actionLocation.SET_LOCATIONS({ locations: updatedLocations }));
			}
		}
	);

	useEffect(() => {
		if (props.itemSelected) {
			const { elementId, yCoordinate } = props.itemSelected;
			if (elementId) {
				const el = document.getElementById(elementId);

				if (el) {
					window.scrollTo({
						top: yCoordinate,
						behavior: "smooth",
					});
					return;
				}
			}
		}
	}, [props.itemSelected]);

	useEffect(() => {
		if (locationId) {
			addSubscription(
				["locationsSubscriptions", locationId],
				[
					{
						context: "onUpdateLocation",
						query: LOCATION_SUBSCRIPTION_QUERY,
						variables: {
							locationId,
						},
					},
				]
			);
		}
	}, [locationId, addSubscription]);

	const isLoading = !props.queryIsFinish || isLoadingOemMetaData;

	return (
		<Grid sx={{ minHeight: "600px", width: "100%" }} className="!w-full !ml-[30px]">
			<Grid>
				{isLoading ? (
					<AdministrationToolsLoader />
				) : (
					<>
						<CollapseArea title="Location" openCollapse={props.itemSelected?.elementId === "Location"} collapsible>
							<LocationDetails />
						</CollapseArea>
						<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />
						<CollapseArea title="Store Picture(s)" openCollapse={props.itemSelected?.elementId === "StorePicture"} collapsible>
							<StorePicture disabled={!StorePictureAction} />
						</CollapseArea>
						<Divider sx={{ borderWidth: "1px", marginLeft: "-0" }} variant="middle" />
						<CollapseArea
							title="Location Dealer Codes"
							openCollapse={props.itemSelected?.elementId === "LocationDealerCodes"}
							collapsible
						>
							<LocationDealerCodes oemMetaData={oemMetaData} locationId={locationId} />
						</CollapseArea>
					</>
				)}
			</Grid>
		</Grid>
	);
}
